/**
 * Money tools for the AI in Ask: it can look over your cards, bank accounts and points, and
 * propose logging a card payment or a new balance. Proposals show as a card with the amount
 * editable, and nothing is saved until you confirm.
 */
import type { EditableField } from "./editable.ts";
import type { ApiCall } from "./tutoringTools.ts";
import { cardStatus, type CardPurchase, type MoneyAccount } from "../money/cards.ts";
import { rewardsFor } from "../money/rewards.ts";
import { cardKindLine, type MoneyProgram } from "../money/cardDetails.ts";
import type { GroupState } from "../types.ts";

export interface MoneyContext {
  accounts: MoneyAccount[];
  programs: MoneyProgram[];
  purchases: Record<string, CardPurchase[]>;
  states: GroupState[];
  today: string;
}

export type MoneyOutcome =
  | { kind: "result"; data: unknown; label: string }
  | {
      kind: "api-proposal";
      summary: string;
      calls: ApiCall[];
      fields: EditableField[];
      label: string;
    }
  | { kind: "error"; message: string; label: string };

const fn = (
  name: string,
  description: string,
  properties: Record<string, unknown>,
  required: string[] = [],
) => ({
  type: "function" as const,
  function: { name, description, parameters: { type: "object", properties, required } },
});

export const MONEY_TOOL_DEFS = [
  fn(
    "money_overview",
    "Money: your cards (owed, available, statement close and due dates, what they're earning this cycle), bank balances and points programs (posted and pending).",
    {},
  ),
  fn(
    "propose_card_payment",
    "Money: propose logging a payment you made to a card (the balance drops by it). The user must confirm.",
    { card: { type: "string" }, amount: { type: "number", description: "Dollars" } },
    ["card", "amount"],
  ),
  fn(
    "propose_balance",
    "Money: propose updating a card's or bank account's balance to what its app shows today. The user must confirm.",
    { account: { type: "string" }, amount: { type: "number", description: "Dollars" } },
    ["account", "amount"],
  ),
] as const;

export const MONEY_TOOL_NAMES = new Set<string>(MONEY_TOOL_DEFS.map((t) => t.function.name));

const money = (c: number) => `${c < 0 ? "-" : ""}$${(Math.abs(c) / 100).toFixed(2)}`;
const micro = (m: number) => money(Math.round(m / 10_000));

function find(list: MoneyAccount[], name: unknown): MoneyAccount | undefined {
  if (typeof name !== "string" || !name.trim()) return undefined;
  const q = name.trim().toLowerCase();
  const live = list.filter((a) => !a.archived);
  const full = (a: MoneyAccount) => `${a.issuer} ${a.name}`.toLowerCase();
  return (
    live.find((a) => a.name.toLowerCase() === q || full(a) === q) ??
    live.find((a) => full(a).includes(q) || (a.last4 && q.includes(a.last4)))
  );
}

export function runMoneyTool(name: string, raw: unknown, ctx: MoneyContext): MoneyOutcome {
  const a = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  if (name === "money_overview") {
    const cards = ctx.accounts
      .filter((x) => x.kind === "credit" && !x.archived)
      .map((x) => {
        const buys = ctx.purchases[x.id] ?? [];
        const s = cardStatus(x, buys, ctx.today);
        const rw = rewardsFor(x, buys, ctx.states, { from: s.lastClose });
        return {
          card: `${x.issuer} ${x.name}`.trim(),
          kind: cardKindLine(x.details, x.issuer) || null,
          owed: micro(s.owedMicro),
          available:
            s.availableMicro === null
              ? x.details?.charge
                ? "no preset limit"
                : null
              : micro(s.availableMicro),
          statementCloses: s.nextClose,
          paymentDue: s.nextDue,
          earningThisCycle: rw
            ? rw.unit === "points"
              ? `${rw.amount} points`
              : money(rw.amount)
            : null,
          business: !!x.details?.business,
          note: x.note || null,
        };
      });
    const banks = ctx.accounts
      .filter((x) => x.kind !== "credit" && !x.archived)
      .map((x) => ({
        account: x.name,
        kind: x.kind,
        balance: money(x.balanceCents),
        asOf: x.balanceOn,
        note: x.note || null,
      }));
    const programs = ctx.programs.map((p) => ({
      program: p.name,
      posted: p.unit === "cash" ? money(p.posted) : `${p.posted} points`,
      pending: p.unit === "cash" ? money(p.pending) : `${p.pending} points`,
      asOf: p.asOf,
    }));
    return {
      kind: "result",
      label: "Looked at your money",
      data: { today: ctx.today, cards, banks, programs },
    };
  }
  if (name === "propose_card_payment" || name === "propose_balance") {
    const acct = find(ctx.accounts, name === "propose_card_payment" ? a.card : a.account);
    if (!acct || (name === "propose_card_payment" && acct.kind !== "credit"))
      return {
        kind: "error",
        label: "Couldn't find that account",
        message: `No ${name === "propose_card_payment" ? "card" : "account"} called ${String(a.card ?? a.account)}.`,
      };
    const cents = Math.round(Number(a.amount) * 100);
    if (!Number.isFinite(cents) || (name === "propose_card_payment" && cents <= 0))
      return {
        kind: "error",
        label: "Odd amount",
        message: "Give the amount in dollars, like 250.",
      };
    const path = `/api/admin/money/${acct.id}/entries`;
    const label = `${acct.issuer} ${acct.name}`.trim();
    if (name === "propose_balance")
      return {
        kind: "api-proposal",
        label: `Proposed a new balance for ${label}`,
        summary: `Set ${label}'s balance to ${money(cents)} as of ${ctx.today}.`,
        calls: [{ table: "money", path, body: { kind: "balance", on: ctx.today, cents } }],
        fields: [
          { key: "0.cents", label: "Balance", kind: "money", value: (cents / 100).toFixed(2) },
        ],
      };
    const owed = Math.round(
      cardStatus(acct, ctx.purchases[acct.id] ?? [], ctx.today).owedMicro / 10_000,
    );
    return {
      kind: "api-proposal",
      label: `Proposed a payment to ${label}`,
      summary: `Log a ${money(cents)} payment to ${label} today (owed ${money(owed)} now, ${money(owed - cents)} after).`,
      calls: [
        { table: "money", path, body: { kind: "payment", on: ctx.today, cents } },
        { table: "money", path, body: { kind: "balance", on: ctx.today, cents: owed - cents } },
      ],
      fields: [
        { key: "0.cents", label: "Payment", kind: "money", value: (cents / 100).toFixed(2) },
      ],
    };
  }
  return { kind: "error", label: `Unknown tool ${name}`, message: "That tool doesn't exist." };
}

/** Keeps a payment's follow-up balance in step when you change the payment amount. */
export function syncPaymentBalance(calls: ApiCall[], before: ApiCall[]): ApiCall[] {
  const pay = calls[0];
  const bal = calls[1];
  const was = before[0];
  if (!pay || !bal || !was || pay.body.kind !== "payment" || bal.body.kind !== "balance")
    return calls;
  const diff = Number(pay.body.cents) - Number(was.body.cents);
  bal.body.cents = Number(bal.body.cents) - diff;
  return calls;
}
